// React
import { useEffect, useState } from "react";
import { Link as ReactRouterLink, useNavigate, useParams } from "react-router-dom";
import React from "react";

// Hooks and Redux
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { refreshMasterData } from "../../../redux/slices/masterDataSlice"

// MUI Components
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';

// MUI Icons
import HomeIcon from '@mui/icons-material/Home';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import BusinessIcon from '@mui/icons-material/Business';

// Utils
import { ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import jwtDecode from "jwt-decode";
import Swal from "sweetalert2";

// Styles
import { green } from "@mui/material/colors";

// Types
import { TokenPayload } from "../../../@types";

// Services
import {
    createCompany,
    updateCompany,
    getCompanyById,
} from "../../../services/MasterDataService";

interface FormData {
    company_code: string;
    company_name: string;
    is_active: boolean;
}

type ModeType = "add" | "edit"

interface MyEditorProps {
  type: ModeType
  editId?: string
}

const ManageCompany: React.FC<MyEditorProps> = ({ type = "add", editId }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const dispatch = useAppDispatch()

  const userRoleType = useAppSelector((state) => state.user.roletype);
    const havePermission = userRoleType === 'Super Admin';

    const [formData, setFormData] = useState<FormData>({ company_code: '', company_name: '', is_active: true });
    const [dataLoading, setDataLoading] = useState<boolean>(isEdit);
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);

    if (!havePermission) {
        navigate('/forbidden', { replace: true });
    }

    useEffect(() => {
        if (!isEdit || !id) return;
        const fetchData = async () => {
            try {
                setDataLoading(true);
                const response = await getCompanyById(id);
                if (response.data?.success === false) {
                    Swal.fire({
                        title: i18n.language === 'th' ? response.data?.message_th : response.data?.message_en,
                        icon: 'error', confirmButtonColor: '#43A047', confirmButtonText: 'OK!',
                        allowOutsideClick: false, allowEscapeKey: false,
                    }).then((result) => {
                        if (result.isConfirmed) navigate('/admin/masterdata/company', { replace: true });
                    });
                    return;
                }
                const item = response.data?.company;
                setFormData({ company_code: item.company_code, company_name: item.company_name, is_active: item.is_active });
            } catch (error) {
                navigate('/servererror', { replace: true });
            } finally {
                setDataLoading(false);
            }
        };
        fetchData();
    }, [id, isEdit, navigate, i18n.language]);

    const formValidationSchema = Yup.object().shape({
      ...(isEdit
        ? {}
        : {
            company_code: Yup.string()
              .required(`${t("Company Code is required")}`)
              .min(4, `${t("Company code must be at least 4 characters")}`)
              .max(4, `${t("Company code must be at most 4 characters")}`)
              .matches(/^[0-9]+$/, `${t("Company code must be number only")}`),
          }),

      company_name: Yup.string().required(`${t("Company Name is required")}`),
    })

    const {
      register,
      handleSubmit,
      formState: { errors },
    } = useForm({ resolver: yupResolver(formValidationSchema) })

    const handleActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, is_active: event.target.checked }))
    }

    const onSubmit = (values: any) => {
      const submit = async () => {
        try {
          const parsedToken: TokenPayload = jwtDecode(localStorage.access_token)
          setSubmitLoading(true)
          let response
          if (isEdit) {
            response = await updateCompany(id as string, {
              company_code: formData.company_code,
              company_name: values.company_name,
              is_active: formData.is_active,
              modified_by: parsedToken.Username,
            })
          } else {
            response = await createCompany({
              company_code: values.company_code,
              company_name: values.company_name,
              is_active: formData.is_active,
              created_by: parsedToken.Username,
            })
          }
          if (response) {
            setSubmitLoading(false)
            const responseData = response.data
            if (!responseData?.success) {
              toast.error(
                i18n.language === "th"
                  ? responseData.message_th
                  : responseData.message_en,
                { position: "top-center", autoClose: 3000, theme: "light" }
              )
            } else {
              Swal.fire({
                title:
                  i18n.language === "th"
                    ? responseData.message_th
                    : responseData.message_en,
                icon: "success",
                confirmButtonColor: "#43A047",
                confirmButtonText: "OK!",
                allowOutsideClick: false,
                allowEscapeKey: false,
              }).then((result) => {
                if (result.isConfirmed) {
                  dispatch(refreshMasterData())
                  navigate("/admin/masterdata/company", { replace: true })
                }
              })
            }
          }
        } catch (error) {
          navigate("/servererror", { replace: true })
        }
      }
      submit()
    }

    if (dataLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "70vh",
          }}
        >
          <CircularProgress />
        </Box>
      )
    }

    return (
      <>
        <ToastContainer style={{ width: "400px" }} />
        {havePermission && (
          <>
            <Box sx={{ height: "auto", maxWidth: "100%", mt: 2 }}>
              <Box display="flex" gap={3} sx={{ mb: 3 }}>
                <Box
                  display="flex"
                  component={ReactRouterLink}
                  onClick={() => window.history.back()}
                  sx={{
                    textDecoration: "none",
                    color: "#0d47a1",
                    alignItems: "center",
                    ":hover": { color: "#1976d2" },
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                  <Typography sx={{ fontWeight: "bold" }}>
                    {t("Back")}
                  </Typography>
                </Box>
                <Breadcrumbs aria-label="breadcrumb">
                  <Link
                    underline="hover"
                    sx={{ display: "flex", alignItems: "center" }}
                    color="inherit"
                    component={ReactRouterLink}
                    to="/admin"
                  >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    {t("Dashboard")}
                  </Link>
                  <Link
                    underline="hover"
                    sx={{ display: "flex", alignItems: "center" }}
                    color="inherit"
                    component={ReactRouterLink}
                    to="/admin/masterdata/company"
                  >
                    <BusinessIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    {t("Company")}
                  </Link>
                  <Typography
                    sx={{ display: "flex", alignItems: "center" }}
                    color="text.primary"
                  >
                    {isEdit ? (
                      <>
                        <EditIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        {t("Edit Company")}
                      </>
                    ) : (
                      <>
                        <AddCircleOutlineIcon
                          sx={{ mr: 0.5 }}
                          fontSize="inherit"
                        />
                        {t("Create New Company")}
                      </>
                    )}
                  </Typography>
                </Breadcrumbs>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                height: "70px",
              }}
            >
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", textAlign: "left" }}
              >
                {isEdit ? t("Edit Company") : t("Create New Company")}
              </Typography>
            </Box>

            <Box
              component="form"
              noValidate
              sx={{ mt: 3 }}
              onKeyDown={(e: React.KeyboardEvent) =>
                e.key === "Enter" && e.preventDefault()
              }
            >
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="company_code"
                    label={t("Company Code")}
                    disabled={isEdit}
                    {...register("company_code")}
                    error={!!(errors as any).company_code}
                    value={isEdit ? formData.company_code : undefined}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setFormData((prev) => ({
                        ...prev,
                        company_code: event.target.value,
                      }))
                      ;(errors as any).company_code = undefined
                    }}
                  />
                  <Typography
                    variant="inherit"
                    color="error"
                    align="left"
                    pt={1}
                    fontSize={13}
                  >
                    {(errors as any).company_code?.message}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="company_name"
                    label={t("Company Name")}
                    {...register("company_name")}
                    error={!!(errors as any).company_name}
                    value={formData.company_name}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setFormData((prev) => ({
                        ...prev,
                        company_name: event.target.value,
                      }))
                      ;(errors as any).company_name = undefined
                    }}
                  />
                  <Typography
                    variant="inherit"
                    color="error"
                    align="left"
                    pt={1}
                    fontSize={13}
                  >
                    {(errors as any).company_name?.message}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    label={t("Active")}
                    control={
                      <Checkbox
                        checked={formData.is_active}
                        onChange={handleActiveChange}
                        inputProps={{ "aria-label": "Active" }}
                      />
                    }
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    sx={{ mt: 5, mb: 2, color: "white" }}
                    onClick={handleSubmit(onSubmit)}
                    disabled={submitLoading}
                  >
                    {isEdit ? t("Save") : t("Create")}
                    {submitLoading && (
                      <CircularProgress
                        size={24}
                        sx={{
                          color: green[500],
                          position: "absolute",
                          zIndex: 999,
                        }}
                      />
                    )}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{ mt: 5, mb: 2 }}
                    component={ReactRouterLink}
                    to="/admin/masterdata/company"
                  >
                    {t("Cancel")}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </>
    )
};

export default ManageCompany;
